import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { cookieCart } from "@/lib/cookieCart";

/**
 * Component that handles redirect logic when user logs in via social auth
 * and has an active cart. Must be placed at the root level to intercept
 * OAuth callbacks before they reach any page.
 */
export const AuthRedirectHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirectedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Only handle SIGNED_IN events
        if (event !== 'SIGNED_IN' || !session?.user) {
          // Reset redirect flag if user signs out
          if (event === 'SIGNED_OUT') {
            hasRedirectedRef.current = false;
            lastUserIdRef.current = null;
          }
          return;
        }

        const user = session.user;
        const currentUserId = user.id;

        const searchParams = new URLSearchParams(window.location.search);
        const hasOAuthQueryParams = searchParams.has('code') || searchParams.has('state');
        // Prevent multiple redirects for the same user
        if (hasRedirectedRef.current && lastUserIdRef.current === currentUserId) {
          console.log('[AuthRedirectHandler] Already redirected for this user, skipping');
          return;
        }

        // Check if this is a social login (OAuth provider)
        const isSocialLogin = user.app_metadata?.provider && 
                             user.app_metadata.provider !== 'email';

        if (!isSocialLogin) {
          console.log('[AuthRedirectHandler] Not a social login, skipping');
          return;
        }

        // Check if user just logged in (check URL hash for OAuth callback)
        const urlParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = urlParams.get('access_token');
        const isOAuthCallback = !!accessToken || window.location.hash.includes('#access_token=') || hasOAuthQueryParams;

        // Only redirect if this is an OAuth callback (new login) or user changed
        const isNewLogin = isOAuthCallback || lastUserIdRef.current !== currentUserId;

        if (!isNewLogin) {
          console.log('[AuthRedirectHandler] Not a new login, skipping redirect');
          return;
        }

        console.log('[AuthRedirectHandler] Social login detected:', {
          provider: user.app_metadata.provider,
          userId: currentUserId,
          isOAuthCallback,
          currentPath: location.pathname
        });

        // Don't redirect if already on cart-sync
        if (location.pathname === '/cart-sync') {
          console.log('[AuthRedirectHandler] Already on cart-sync, skipping redirect');
          hasRedirectedRef.current = true;
          lastUserIdRef.current = currentUserId;
          return;
        }
        
        // If on /auth page, only redirect if it's an OAuth callback (has hash)
        if (location.pathname === '/auth' && !isOAuthCallback) {
          console.log('[AuthRedirectHandler] On /auth but not OAuth callback, skipping redirect');
          hasRedirectedRef.current = true;
          lastUserIdRef.current = currentUserId;
          return;
        }

        const clearOAuthParams = () => {
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.delete('code');
          url.searchParams.delete('state');
          window.history.replaceState(null, '', url.pathname + url.search);
        };

        // Check if there's an active cart
        const localCart = cookieCart.get();
        const hasActiveCart = localCart && localCart.items && localCart.items.length > 0;

        console.log('[AuthRedirectHandler] Cart check:', {
          hasActiveCart,
          itemCount: localCart?.items.length || 0,
          cart: localCart
        });

        if (hasActiveCart) {
          console.log('[AuthRedirectHandler] Active cart detected, redirecting to cart-sync');
          hasRedirectedRef.current = true;
          lastUserIdRef.current = currentUserId;
          
          // Clear the OAuth hash from URL
          clearOAuthParams();
          
          navigate('/cart-sync', { replace: true });
        } else {
          console.log('[AuthRedirectHandler] No active cart, redirecting to stack-selection');
          hasRedirectedRef.current = true;
          lastUserIdRef.current = currentUserId;
          
          // Clear the OAuth hash from URL
          clearOAuthParams();
          
          // Redirect to stack-selection if not already there
          if (location.pathname !== '/stack-selection') {
            navigate('/stack-selection', { replace: true });
          }
        }
      }
    );

    // Check on mount if there's an OAuth callback in the URL
    // This handles the case where user returns from OAuth provider
    // but the onAuthStateChange hasn't fired yet
    const checkOAuthCallback = () => {
      const hasOAuthHash = window.location.hash.includes('access_token=') || 
                          window.location.hash.includes('#access_token=');
      const hasOAuthQueryParams = window.location.search.includes('code=') || 
                                 window.location.search.includes('state=');
      
      if ((hasOAuthHash || hasOAuthQueryParams) && location.pathname !== '/cart-sync') {
        // Wait a bit for auth state to update, then check
        setTimeout(async () => {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
            const isSocialLogin = session.user.app_metadata?.provider && 
                                 session.user.app_metadata.provider !== 'email';
            
            if (isSocialLogin && lastUserIdRef.current !== session.user.id) {
              const localCart = cookieCart.get();
              const hasActiveCart = localCart && localCart.items && localCart.items.length > 0;
              
              if (hasActiveCart) {
                console.log('[AuthRedirectHandler] OAuth callback detected with active cart, redirecting');
                lastUserIdRef.current = session.user.id;
                hasRedirectedRef.current = true;
                
                // Clear the OAuth params from URL
                const url = new URL(window.location.href);
                url.hash = '';
                url.searchParams.delete('code');
                url.searchParams.delete('state');
                window.history.replaceState(null, '', url.pathname + url.search);
                
                navigate('/cart-sync', { replace: true });
              } else {
                console.log('[AuthRedirectHandler] OAuth callback detected without cart, redirecting to stack-selection');
                lastUserIdRef.current = session.user.id;
                hasRedirectedRef.current = true;
                
                // Clear OAuth params from URL
                const url = new URL(window.location.href);
                url.hash = '';
                url.searchParams.delete('code');
                url.searchParams.delete('state');
                window.history.replaceState(null, '', url.pathname + url.search);
                
                // Redirect to stack-selection if not already there
                if (location.pathname !== '/stack-selection') {
                  navigate('/stack-selection', { replace: true });
                }
              }
            }
          }
        }, 500);
      }
    };

    checkOAuthCallback();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  // This component doesn't render anything
  return null;
};
